/**
 * GAME CONFIGURATIONS (JSON Schema keyed by brand)
 * Defines metadata, target options, themes, and game-specific settings options
 * for: 'crazy', 'clash', 'pints', and 'adult'.
 *
 * Designed for single-engine rendering, saving, loading, validation, and randomization.
 */

(function (root, factory) {
  var exp = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = exp;
  }
  if (typeof root !== 'undefined') {
    root.GAME_CONFIGS = exp;
  }
  if (typeof window !== 'undefined') {
    window.GAME_CONFIGS = exp;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const GAME_CONFIGS = {
    crazy: {
      id: 'crazy',
      name: 'Crazy Cribbage',
      title: 'CRAZY CRIBBAGE',
      tagline: 'THE WILD TWIST ON CLASSIC CRIBBAGE',
      startButtonText: 'GO CRAZY',
      startAriaLabel: 'Start Game',
      logo: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo_-_640_x_640.webp',
        alt: 'Crazy Cribbage'
      },
      theme: {
        defaultTheme: 'tournament',
        locked: false,
        storageKey: 'crazyCribbage.theme'
      },
      storageKey: 'crazyCribbage.settings.v1',
      recordKey: 'crazyCribbage.records.v1',
      ageRestricted: false,
      allowCpu: true,
      allowGoalSelect: true,
      defaultGoal: 'high',
      defaultTarget: 121,
      targets: [
        { value: 121, label: 'Full Game (121)' },
        { value: 61, label: 'Quick game (61)' },
        { value: 31, label: 'Pegging Only (31)' }
      ],
      randomizer: {
        mode: 'subset',
        buttonText: 'Randomize Bonus Options',
        fullRandomText: null
      },
      subgroups: [
        { id: 'general', title: 'General Options' },
        { id: 'cards', title: 'Individual Cards' },
        { id: 'poker', title: 'Poker Hands' },
        { id: 'pegging', title: 'Pegging Options' },
        { id: 'hands', title: 'Hands & Cribs' }
      ],
      options: [
        // General Options
        { key: 'jokers', chkId: 'optJokers', subgroup: 'general', type: 'boolean', name: 'Jokers', description: '2 wild cards per deck; represent any card in play, hands & crib', default: false },
        { key: 'optCutBeforeDiscard', chkId: 'optCutBeforeDiscard', subgroup: 'general', type: 'boolean', name: 'Cut before Discard', description: 'Pone cuts the starter card before players discard to the crib', default: false },
        { key: 'opt31sFor5', chkId: 'opt31sFor5', subgroup: 'general', type: 'boolean', name: "31's for 5", description: 'Combinations summing to 31 count for 5 bonus points in play, hand & crib', default: false },
        { key: 'opt19sFor2', chkId: 'opt19sFor2', subgroup: 'general', type: 'boolean', name: "19's for 2", description: 'Combinations summing to 19 count for 2 bonus points in play, hand & crib', default: false },
        { key: 'optRoundAroundTheWorld', chkId: 'optRoundAroundTheWorld', subgroup: 'general', type: 'boolean', name: 'Round around the World', description: 'Runs can wrap around Ace to King (e.g. K-A-2 or Q-K-A)', default: false },
        { key: 'optAllRed', chkId: 'optAllRed', subgroup: 'general', type: 'boolean', name: 'All Red', description: 'All-red cards in hand, crib, or pegging score 1 bonus point per card', default: false },
        { key: 'optAllBlack', chkId: 'optAllBlack', subgroup: 'general', type: 'boolean', name: 'All Black', description: 'All-black cards in hand, crib, or pegging score 1 bonus point per card', default: false },
        { key: 'optAllOdd', chkId: 'optAllOdd', subgroup: 'general', type: 'boolean', name: 'All Odd', description: 'All odd ranks (A, 3, 5, 7, 9, J, K) score 1 bonus point per card', default: false },
        { key: 'optAllEven', chkId: 'optAllEven', subgroup: 'general', type: 'boolean', name: 'All Even', description: 'All even ranks (2, 4, 6, 8, 10, Q) score 1 bonus point per card', default: false },
        { key: 'optAllDifferentSuits', chkId: 'optAllDifferentSuits', subgroup: 'general', type: 'boolean', name: 'All Different Suits', description: 'All cards having distinct suits score 1 bonus point per card', default: false },
        { key: 'optFlushInPlay', chkId: 'optFlushInPlay', subgroup: 'general', type: 'boolean', name: 'Flush in Play', description: '3 or more consecutive cards of the same suit in pegging score 1 pt per card', default: false },
        { key: 'optChutesAndLadders', chkId: 'optChutesAndLadders', subgroup: 'general', type: 'boolean', name: 'Chutes and Ladders', description: 'If at any time all players are tied, everyone goes back to the start (hole 0)', default: false },

        // Individual Cards
        { key: 'optKH', chkId: 'optKH', subgroup: 'cards', type: 'boolean', name: 'King of Hearts (Suicide King)', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
        { key: 'optQS', chkId: 'optQS', subgroup: 'cards', type: 'boolean', name: 'Queen of Spades', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
        { key: 'optAS', chkId: 'optAS', subgroup: 'cards', type: 'boolean', name: 'Ace of Spades', description: 'Scores 5 bonus points when in hand or crib (does not count as starter)', default: false },
        { key: 'optSpades', chkId: 'optSpades', subgroup: 'cards', type: 'boolean', name: 'Spades', description: 'Hands & cribs with a Spade double; no Spade loses 29 pts (crib immune if dealer holds a Spade; min 0). Cutting a Spade doubles pegging & heels', default: false },
        { key: 'optGuessStarter', chkId: 'optGuessStarter', subgroup: 'cards', type: 'boolean', name: 'Guess Starter', description: 'Pone guesses the rank of the cut starter card before it is turned over. A correct guess scores 3 points', default: false },

        // Poker Hands
        { key: 'optStraight', chkId: 'optStraight', subgroup: 'poker', type: 'boolean', name: 'Straight', description: 'A run of 5 or more cards scores 10 bonus points', default: false },
        { key: 'optFullHouse', chkId: 'optFullHouse', subgroup: 'poker', type: 'boolean', name: 'Full House', description: 'Three of a kind plus a pair in hand or crib scores 10 bonus points', default: false },
        { key: 'optStraightFlush', chkId: 'optStraightFlush', subgroup: 'poker', type: 'boolean', name: 'Straight Flush', description: 'Five consecutive cards of the same suit score 20 bonus points', default: false },
        { key: 'optRoyalFlush', chkId: 'optRoyalFlush', subgroup: 'poker', type: 'boolean', name: 'Royal Flush', description: '10-J-Q-K-A of the same suit scores 30 bonus points', default: false },

        // Pegging Options
        { key: 'optMultipleRunsInPegging', chkId: 'optMultipleRunsInPegging', subgroup: 'pegging', type: 'boolean', name: 'Multiple Runs in Pegging', description: 'Double runs, triple runs and quadruple runs can be scored during pegging', default: false },
        { key: 'optPegThroughZones', chkId: 'optPegThroughZones', subgroup: 'pegging', type: 'boolean', name: 'Peg Through Zones', description: "Holes 31–40, 61–70, 91–100 are peg-only zones; hand & crib points don't count", default: false, mutualGroup: 'zones' },
        { key: 'optLast5PegOut', chkId: 'optLast5PegOut', subgroup: 'pegging', type: 'boolean', name: 'Last 5 Peg Out', description: "Last 5 holes require pegging out; hand & crib points cannot cross finish", default: false, mutualGroup: 'zones' },
        { key: 'optBunkers', chkId: 'optBunkers', subgroup: 'pegging', type: 'boolean', name: 'Bunkers', description: 'First & last 5 holes, plus 50–60 & 80–90; pegging points only', default: false, mutualGroup: 'zones' },
        { key: 'optStinkHole', chkId: 'optStinkHole', subgroup: 'pegging', type: 'boolean', name: 'Stink Hole', description: 'At hole 120 (or 60 in quick game), you must score exactly 1 point to win', default: false, mutualGroup: 'zones' },
        { key: 'optProgressiveSteals', chkId: 'optProgressiveSteals', subgroup: 'pegging', type: 'boolean', name: 'Progressive Steals', description: "Pair opponent's card while pegging to steal it into your hand for counting", default: false },

        // Hands & Cribs
        { key: 'optStolenCrib', chkId: 'optStolenCrib', subgroup: 'hands', type: 'boolean', name: 'Stolen Crib', description: "Pone guesses dealer's exact crib points to steal all of them (2 players only)", default: false, requiredPlayers: 2 },
        { key: 'optZeroHand', chkId: 'optZeroHand', subgroup: 'hands', type: 'boolean', name: 'Zero Hand', description: 'A 0-point hand inflicts −19 penalty points', default: false },
        { key: 'optZeroCrib', chkId: 'optZeroCrib', subgroup: 'hands', type: 'boolean', name: 'Zero Crib', description: 'A 0-point crib inflicts −19 penalty points to dealer', default: false },
        { key: 'optRockCrib', chkId: 'optRockCrib', subgroup: 'hands', type: 'boolean', name: 'Rock the Crib', description: 'All crib points subtract from dealer score instead of adding', default: false },
        { key: 'optPickYourCrib', chkId: 'optPickYourCrib', subgroup: 'hands', type: 'boolean', name: 'Pick Your Crib', description: "Dealer discards normally, but steals card(s) from pone's hand for the crib", default: false },
        { key: 'optSpeedoCribbage', chkId: 'optSpeedoCribbage', subgroup: 'hands', type: 'boolean', name: 'Speedo Cribbage', description: "Pone discards to dealer's hand; dealer discards 4 (or 6 in super) to crib (2 players only)", default: false, requiredPlayers: 2 },
        { key: 'optShotgunCribbage', chkId: 'optShotgunCribbage', subgroup: 'hands', type: 'boolean', name: 'Shotgun Cribbage', description: 'Dealer forfeits all points scored in the crib', default: false },
        { key: 'optTwistedCribbage', chkId: 'optTwistedCribbage', subgroup: 'hands', type: 'boolean', name: 'Twisted Cribbage', description: 'Dealer combines 8 cards to make best two 4-card hands. Flushes require all 5 cards matching (2 players only)', default: false, requiredPlayers: 2 },
        { key: 'optFivesCribbage', chkId: 'optFivesCribbage', subgroup: 'hands', type: 'boolean', name: "Five's Cribbage", description: 'Players must discard any 5s in hand directly to the crib', default: false },
        { key: 'optSevensAndEightsCribbage', chkId: 'optSevensAndEightsCribbage', subgroup: 'hands', type: 'boolean', name: 'Sevens and Eights Cribbage', description: 'Players must discard any 7s and 8s in hand directly to the crib', default: false },
        { key: 'optDrawCribbage', chkId: 'optDrawCribbage', subgroup: 'hands', type: 'boolean', name: 'Draw Cribbage', description: 'Draw 0–5 cards after deal, discard 2 to crib, then discard & draw 0–3 cards (2 players only)', default: false, requiredPlayers: 2 },
        { key: 'opt7CardCribbage', chkId: 'opt7CardCribbage', subgroup: 'hands', type: 'boolean', name: '7 Card Cribbage', description: '7 cards dealt, 1 dealt to crib, both players discard 2 to crib (2 players only)', default: false, requiredPlayers: 2 },
        { key: 'optProgressiveCut', chkId: 'optProgressiveCut', subgroup: 'hands', type: 'boolean', name: 'Progressive cut', description: 'Cut 1 card for first 2 hands, 2 for next 2, etc. Game ends when >121 with equal cribs (2 players only)', default: false, requiredPlayers: 2 },
        { key: 'optCanadianDoubles', chkId: 'optCanadianDoubles', subgroup: 'hands', type: 'boolean', name: 'Canadian Doubles', description: '4-player partnership: dealer and pone get 10 cards, keep 4, pass 4 to partner, discard 2 to crib (forces 4 players partnership)', default: false, requiredPlayers: 4, requiredGameMode: 'partnership', requiredPlayerMode: '4_part' }
      ]
    },

    clash: {
      id: 'clash',
      name: 'Cribbage Clash',
      title: 'CRIBBAGE CLASH',
      tagline: 'TACTICAL BOARD BATTLE CRIBBAGE',
      startButtonText: 'BEGIN BATTLE',
      startAriaLabel: 'Begin Battle',
      logo: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-round-online-game-640x640.webp',
        alt: 'Cribbage Clash'
      },
      theme: {
        defaultTheme: 'rustic',
        locked: true,
        storageKey: 'cribbageClash.theme'
      },
      storageKey: 'cribbageClash.settings.v1',
      recordKey: 'cribbageClash.records.v1',
      ageRestricted: false,
      allowCpu: true,
      allowGoalSelect: false,
      defaultGoal: 'high',
      defaultTarget: 481,
      targets: [
        { value: 241, label: '241 - Short' },
        { value: 361, label: '361 - Medium' },
        { value: 481, label: '481 - Long' }
      ],
      randomizer: {
        mode: 'clashTactical',
        buttonText: 'Randomize',
        fullRandomText: 'Full Random',
        description: 'Randomize options, counts, non-overlapping placements & signs'
      },
      subgroups: [
        { id: 'clash', title: 'Cribbage Clash' }
      ],
      options: [
        {
          key: 'noPassingLanes',
          chkId: 'optNoPassingLanes',
          countId: 'optNoPassingLaneCount',
          prefix: 'optNoPassingLane',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'No Passing Lanes',
          description: 'single-file bottlenecks where players cannot pass',
          badgePrefix: 'Lane',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 5,
          headerDesc: '5 holes in length · bottlenecks into middle row',
          holes: { min: 10, max: 100, step: 5, defaults: [20, 50, 80, 100], suffix: 'Start' }
        },
        {
          key: 'jump',
          chkId: 'optJump',
          countId: 'optJumpCount',
          prefix: 'optJump',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Jump',
          description: 'Vault players forward (10 holes) or backward (20 holes)',
          badgePrefix: 'Jump',
          maxCount: 8,
          defaultCount: 2,
          lengthType: 'jumpDynamic', // 1 if forward, 3 if backward
          headerDesc: 'Forward jumps are 1 hole · Backward jumps are 3 holes · landing triggers a leap',
          hasDestination: true,
          holes: { min: 5, max: 115, step: 5, defaults: [15, 65, 5, 115, 25, 75, 45, 95], suffix: 'Start' },
          destHoles: { min: 5, max: 120, step: 5, defaults: [35, 50, 45, 70, 55, 60, 80, 85], suffix: 'Dest' }
        },
        {
          key: 'jail',
          chkId: 'optJail',
          countId: 'optJailCount',
          prefix: 'optJail',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Jail',
          description: 'Traps players until doubles or a jack is cut',
          badgePrefix: 'Jail',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 110 · Must cut a Jack or roll doubles to exit',
          holes: { min: 10, max: 110, step: 5, defaults: [25, 75, 45, 95], suffix: 'Hole' }
        },
        {
          key: 'bar',
          chkId: 'optBar',
          countId: 'optBarCount',
          prefix: 'optBar',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'The Bar',
          description: 'Social lounge with 1-turn pause or beverage challenge',
          badgePrefix: 'Bar',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 110 · Social lounge with 1-turn pause or beverage challenge',
          holes: { min: 10, max: 110, step: 5, defaults: [40, 85, 60, 105], suffix: 'Hole' }
        },
        {
          key: 'timeout',
          chkId: 'optTimeout',
          countId: 'optTimeoutCount',
          prefix: 'optTimeout',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Timeout',
          description: 'Penalty box benching players for 1 turn',
          badgePrefix: 'Timeout',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 110 · Penalty box benching players for 1 turn',
          holes: { min: 10, max: 110, step: 5, defaults: [30, 90, 50, 110], suffix: 'Hole' }
        },
        {
          key: 'detour',
          chkId: 'optDetour',
          countId: 'optDetourCount',
          prefix: 'optDetour',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Detour',
          description: '1-hole traps divert players into an 8-hole scenic loop returning to same hole',
          badgePrefix: 'Detour',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 110 · 8-hole off-track detour loop returning to same hole',
          holes: { min: 10, max: 110, step: 5, defaults: [10, 60, 35, 80], suffix: 'Hole' }
        },
        {
          key: 'roundAbout',
          chkId: 'optRoundAbout',
          countId: 'optRoundAboutCount',
          prefix: 'optRoundAbout',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Round About',
          description: '1-hole triggers divert players into a 7-peg rotary loop returning 1 hole ahead',
          badgePrefix: 'Round About',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 110 · 7-peg off-track rotary loop returning 1 hole ahead',
          holes: { min: 10, max: 110, step: 5, defaults: [35, 70, 55, 90], suffix: 'Hole' }
        },
        {
          key: 'signs',
          chkId: 'optSigns',
          countId: 'optSignsCount',
          prefix: 'optSigns',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Signs',
          description: '3-peg (2 signs) or 5-peg (4 signs) blocks with interactive sign selection and replenishing pools',
          badgePrefix: 'Sign Block',
          maxCount: 8,
          defaultCount: 2,
          lengthType: 'signsSize', // 3 if size=2, 5 if size=4
          headerDesc: '3 pegs (2 signs) or 5 pegs (4 signs) · 10 to 110 · Replenishes when empty',
          hasSignsSlots: true,
          holes: { min: 10, max: 110, step: 5, defaults: [45, 55, 95, 105, 30, 65, 85, 115], suffix: 'Hole' }
        },
        {
          key: 'stopSign',
          chkId: 'optStopSign',
          countId: 'optStopSignCount',
          prefix: 'optStopSign',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Stop Sign',
          description: '1-hole traps where counting stops immediately',
          badgePrefix: 'Stop Sign',
          maxCount: 8,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 5 to 475 · When reached, counting stops immediately (pegging, hand, or crib)',
          holes: { min: 5, max: 115, step: 5, defaults: [20, 35, 50, 65, 80, 95, 105, 115], suffix: 'Hole' }
        },
        {
          key: 'dice',
          chkId: 'optDice',
          countId: 'optDiceCount',
          prefix: 'optDice',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Dice',
          description: 'Interactive 3-die challenge spaces triggering a Roll The Dice roll (±5 to ±30 points)',
          badgePrefix: 'Dice',
          color: '#a855f7',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 475 · Triggers an animated 3-die roll for points and target player',
          holes: { min: 10, max: 110, step: 5, defaults: [50, 85, 200, 320], suffix: 'Hole' }
        },
        {
          key: 'wheel',
          chkId: 'optWheel',
          countId: 'optWheelCount',
          prefix: 'optWheel',
          subgroup: 'clash',
          type: 'trackItem',
          name: 'Spinning Wheel',
          icon: '🎡',
          description: 'Carnival prize wheel spaces triggering an animated Spin-The-Wheel spin for exciting rewards or penalties',
          badgePrefix: 'Spinning Wheel',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 2,
          lengthType: 'fixed',
          fixedLength: 1,
          headerDesc: '1 peg long · 10 to 475 · Triggers an animated wheel spin for points, jumps, or penalties',
          holes: { min: 10, max: 110, step: 5, defaults: [65, 160, 260, 385], suffix: 'Hole' }
        },
        {
          key: 'skunkMarkers',
          chkId: 'optSkunkMarkers',
          prefix: 'optSkunkMarker',
          subgroup: 'clash',
          type: 'skunkSpecial',
          name: 'Skunk Markers',
          description: 'Display interactive 75% and 50% Skunk Markers directly on the game board'
        }
      ]
    },

    pints: {
      id: 'pints',
      name: 'Pints and Pegs',
      title: 'PINTS AND PEGS',
      tagline: 'THE DRINKING CRIBBAGE PUB EXPERIENCE',
      startButtonText: 'PLAY PINTS',
      startAriaLabel: 'Play Pints',
      logo: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-pints-and-pegs_640x640_f004d2b4-49df-4181-a6d2-11c7f9212645.webp',
        alt: 'Pints and Pegs'
      },
      theme: {
        defaultTheme: 'tournament',
        locked: false,
        storageKey: 'crazyCribbage.theme'
      },
      storageKey: 'pintsAndPegs.settings.v1',
      recordKey: 'pintsAndPegs.records.v1',
      ageRestricted: true,
      allowCpu: false,
      ageGateMessage: 'This version contains drinking themes and pub games intended solely for individuals of legal drinking age. You must be at least 21 years old to enter and play.',
      allowGoalSelect: true,
      defaultGoal: 'high',
      defaultTarget: 121,
      targets: [
        { value: 121, label: 'Full Game (121)' },
        { value: 61, label: 'Quick game (61)' },
        { value: 31, label: 'Pegging Only (31)' }
      ],
      randomizer: {
        mode: 'trackDistribution',
        totalBoardSpaces: 24,
        step: 5,
        minHole: 5,
        maxHole: 120,
        buttonText: 'Randomize',
        fullRandomText: 'Full Random',
        description: 'Randomize drink spaces, counts & non-overlapping placements'
      },
      subgroups: [
        { id: 'pints', title: 'Pints and Pegs' }
      ],
      options: [
        {
          key: 'youDrink',
          chkId: 'optYouDrink',
          countId: 'optYouDrinkCount',
          pegsId: 'optYouDrinkPegs',
          prefix: 'optYouDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'You Drink',
          description: 'Take a drink when you land on this space',
          actionText: 'Landing here requires the player to take a drink',
          icon: '🍺',
          color: '#f59e0b',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [15, 45, 75, 105], suffix: 'Hole' }
        },
        {
          key: 'guysDrink',
          chkId: 'optGuysDrink',
          countId: 'optGuysDrinkCount',
          pegsId: 'optGuysDrinkPegs',
          prefix: 'optGuysDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Guys Drink',
          description: 'All male players take a drink',
          actionText: 'Landing here requires all guys to take a drink',
          icon: '🍻',
          color: '#3b82f6',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [25, 55, 85, 115], suffix: 'Hole' }
        },
        {
          key: 'galsDrink',
          chkId: 'optGalsDrink',
          countId: 'optGalsDrinkCount',
          pegsId: 'optGalsDrinkPegs',
          prefix: 'optGalsDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Gals Drink',
          description: 'All female players take a drink',
          actionText: 'Landing here requires all gals to take a drink',
          icon: '🍷',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [35, 65, 95, 110], suffix: 'Hole' }
        },
        {
          key: 'leftDrink',
          chkId: 'optLeftDrink',
          countId: 'optLeftDrinkCount',
          pegsId: 'optLeftDrinkPegs',
          prefix: 'optLeftDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Player to the left Drinks',
          description: 'Person to your left takes a drink',
          actionText: 'Landing here requires the player to your left to take a drink',
          icon: '👈',
          color: '#8b5cf6',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [10, 40, 70, 100], suffix: 'Hole' }
        },
        {
          key: 'rightDrink',
          chkId: 'optRightDrink',
          countId: 'optRightDrinkCount',
          pegsId: 'optRightDrinkPegs',
          prefix: 'optRightDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Player to the right Drinks',
          description: 'Person to your right takes a drink',
          actionText: 'Landing here requires the player to your right to take a drink',
          icon: '👉',
          color: '#06b6d4',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [20, 50, 80, 120], suffix: 'Hole' }
        },
        {
          key: 'everyoneDrink',
          chkId: 'optEveryoneDrink',
          countId: 'optEveryoneDrinkCount',
          pegsId: 'optEveryoneDrinkPegs',
          prefix: 'optEveryoneDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Everyone Drinks',
          description: 'Social! Every player at the table takes a drink',
          actionText: 'Landing here requires everyone playing to take a drink',
          icon: '🥂',
          color: '#10b981',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [30, 60, 90, 5], suffix: 'Hole' }
        },
        {
          key: 'everyoneButYouDrink',
          chkId: 'optEveryoneButYouDrink',
          countId: 'optEveryoneButYouDrinkCount',
          pegsId: 'optEveryoneButYouDrinkPegs',
          prefix: 'optEveryoneButYouDrink',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'Everyone but you Drinks',
          description: 'Everyone except the pegging player takes a drink',
          actionText: 'Landing here requires everyone except you to take a drink',
          icon: '🎉',
          color: '#f97316',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [20, 50, 80, 110], suffix: 'Hole' }
        },
        {
          key: 'pickWhoDrinks',
          chkId: 'optPickWhoDrinks',
          countId: 'optPickWhoDrinksCount',
          pegsId: 'optPickWhoDrinksPegs',
          prefix: 'optPickWhoDrinks',
          subgroup: 'pints',
          type: 'trackItem',
          name: 'You pick who drinks',
          description: 'Choose any single opponent or player to take a drink',
          actionText: 'Landing here allows you to choose who takes a drink',
          icon: '🎯',
          color: '#a855f7',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          holes: { min: 5, max: 120, step: 5, defaults: [25, 55, 85, 115], suffix: 'Hole' }
        }
      ]
    },

    adult: {
      id: 'adult',
      name: 'Skunked and Stripped',
      title: 'SKUNKED AND STRIPPED',
      tagline: 'STRIP CRIBBAGE & MATURE CHALLENGES',
      startButtonText: 'GET STRIPPED',
      startAriaLabel: 'Get Stripped',
      logo: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-skunked-and-stripped_-_640_x_640.webp?v=1791397387',
        alt: 'Skunked and Stripped'
      },
      theme: {
        defaultTheme: 'passion',
        locked: false,
        storageKey: 'skunkedAndStripped.theme'
      },
      storageKey: 'skunkedAndStripped.settings.v1',
      recordKey: 'skunkedAndStripped.records.v1',
      ageRestricted: true,
      allowCpu: false,
      ageGateMessage: 'This version contains mature adult themes and erotic challenges intended solely for adults. You must be at least 21 years old to enter and play.',
      allowGoalSelect: true,
      defaultGoal: 'high',
      defaultTarget: 121,
      targets: [
        { value: 121, label: 'Full Game (121)' },
        { value: 61, label: 'Quick game (61)' },
        { value: 31, label: 'Pegging Only (31)' }
      ],
      randomizer: {
        mode: 'trackDistribution',
        totalBoardSpaces: 24,
        step: 5,
        minHole: 5,
        maxHole: 120,
        buttonText: 'Randomize',
        fullRandomText: 'Full Random',
        description: 'Randomize adult spaces, counts & non-overlapping placements'
      },
      subgroups: [
        { id: 'strip', title: 'Strip Themed Options' },
        { id: 'adultChallenges', title: 'Adult Challenges (Timed)' }
      ],
      options: [
        // Group 1: Strip Themed (2 spaces default)
        {
          key: 'loseClothing',
          chkId: 'optLoseClothing',
          countId: 'optLoseClothingCount',
          pegsId: 'optLoseClothingPegs',
          prefix: 'optLoseClothing',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'You Lose Clothing',
          description: 'You lose an article of clothing',
          actionText: 'Player loses an article of clothing',
          group: 'strip',
          icon: '👙',
          color: '#f43f5e',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [10, 70, 10, 70], suffix: 'Hole' }
        },
        {
          key: 'gainClothing',
          chkId: 'optGainClothing',
          countId: 'optGainClothingCount',
          pegsId: 'optGainClothingPegs',
          prefix: 'optGainClothing',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'You Gain Clothing',
          description: 'You gain an article of clothing',
          actionText: 'Player gains an article of clothing back',
          group: 'strip',
          icon: '🧥',
          color: '#10b981',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [20, 80, 20, 80], suffix: 'Hole' }
        },
        {
          key: 'pickWhoLosesClothing',
          chkId: 'optPickWhoLosesClothing',
          countId: 'optPickWhoLosesClothingCount',
          pegsId: 'optPickWhoLosesClothingPegs',
          prefix: 'optPickWhoLosesClothing',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'Pick Who Strips',
          description: 'You pick who loses an article of clothing',
          actionText: 'Player picks who loses an article of clothing',
          group: 'strip',
          icon: '🎯',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [30, 90, 30, 90], suffix: 'Hole' }
        },
        {
          key: 'rightLosesClothing',
          chkId: 'optRightLosesClothing',
          countId: 'optRightLosesClothingCount',
          pegsId: 'optRightLosesClothingPegs',
          prefix: 'optRightLosesClothing',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'Right Neighbor Strips',
          description: 'Person to the right loses an article of clothing',
          actionText: 'Person to right loses an article of clothing',
          group: 'strip',
          icon: '👉',
          color: '#3b82f6',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [40, 100, 40, 100], suffix: 'Hole' }
        },
        {
          key: 'leftLosesClothing',
          chkId: 'optLeftLosesClothing',
          countId: 'optLeftLosesClothingCount',
          pegsId: 'optLeftLosesClothingPegs',
          prefix: 'optLeftLosesClothing',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'Left Neighbor Strips',
          description: 'Person to the left loses an article of clothing',
          actionText: 'Person to left loses an article of clothing',
          group: 'strip',
          icon: '👈',
          color: '#8b5cf6',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [50, 110, 50, 110], suffix: 'Hole' }
        },
        {
          key: 'removeClothingChoice',
          chkId: 'optRemoveClothingChoice',
          countId: 'optRemoveClothingChoiceCount',
          pegsId: 'optRemoveClothingChoicePegs',
          prefix: 'optRemoveClothingChoice',
          subgroup: 'strip',
          type: 'trackItem',
          name: 'Remove Clothing Choice',
          description: 'Remove an article of clothing off person of choice',
          actionText: 'Remove an article of clothing off person of your choice',
          group: 'strip',
          icon: '✂️',
          color: '#e11d48',
          maxCount: 4,
          defaultCount: 2,
          defaultPegs: 1,
          timed: false,
          holes: { min: 5, max: 120, step: 5, defaults: [60, 120, 60, 120], suffix: 'Hole' }
        },

        // Group 2: Adult Themed Timed Challenges (1 space default)
        {
          key: 'touchBreast',
          chkId: 'optTouchBreast',
          countId: 'optTouchBreastCount',
          pegsId: 'optTouchBreastPegs',
          prefix: 'optTouchBreast',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Touch Breast',
          description: 'Touch breast (60s timer)',
          actionText: 'Touch breast',
          group: 'adult',
          icon: '💋',
          color: '#f43f5e',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [5, 45, 75, 105], suffix: 'Hole' }
        },
        {
          key: 'lickBreast',
          chkId: 'optLickBreast',
          countId: 'optLickBreastCount',
          pegsId: 'optLickBreastPegs',
          prefix: 'optLickBreast',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Lick Breast',
          description: 'Lick breast (60s timer)',
          actionText: 'Lick breast',
          group: 'adult',
          icon: '👅',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [15, 50, 80, 110], suffix: 'Hole' }
        },
        {
          key: 'suckBreast',
          chkId: 'optSuckBreast',
          countId: 'optSuckBreastCount',
          pegsId: 'optSuckBreastPegs',
          prefix: 'optSuckBreast',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Suck Breast',
          description: 'Suck breast (60s timer)',
          actionText: 'Suck breast',
          group: 'adult',
          icon: '👄',
          color: '#d946ef',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [25, 55, 85, 115], suffix: 'Hole' }
        },
        {
          key: 'touchGenitals',
          chkId: 'optTouchGenitals',
          countId: 'optTouchGenitalsCount',
          pegsId: 'optTouchGenitalsPegs',
          prefix: 'optTouchGenitals',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Touch Genitals',
          description: 'Touch genitals (60s timer)',
          actionText: 'Touch genitals',
          group: 'adult',
          icon: '✨',
          color: '#a855f7',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [35, 40, 70, 100], suffix: 'Hole' }
        },
        {
          key: 'lickGenitals',
          chkId: 'optLickGenitals',
          countId: 'optLickGenitalsCount',
          pegsId: 'optLickGenitalsPegs',
          prefix: 'optLickGenitals',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Lick Genitals',
          description: 'Lick genitals (60s timer)',
          actionText: 'Lick genitals',
          group: 'adult',
          icon: '👅',
          color: '#f43f5e',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [45, 60, 90, 120], suffix: 'Hole' }
        },
        {
          key: 'suckGenitals',
          chkId: 'optSuckGenitals',
          countId: 'optSuckGenitalsCount',
          pegsId: 'optSuckGenitalsPegs',
          prefix: 'optSuckGenitals',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Suck Genitals',
          description: 'Suck genitals (60s timer)',
          actionText: 'Suck genitals',
          group: 'adult',
          icon: '👄',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [55, 65, 95, 115], suffix: 'Hole' }
        },
        {
          key: 'fingerGenitals',
          chkId: 'optFingerGenitals',
          countId: 'optFingerGenitalsCount',
          pegsId: 'optFingerGenitalsPegs',
          prefix: 'optFingerGenitals',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Finger Genitals',
          description: 'Finger genitals (60s timer)',
          actionText: 'Finger genitals',
          group: 'adult',
          icon: '👉',
          color: '#f59e0b',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [65, 45, 75, 105], suffix: 'Hole' }
        },
        {
          key: 'genitalIntercourse',
          chkId: 'optGenitalIntercourse',
          countId: 'optGenitalIntercourseCount',
          pegsId: 'optGenitalIntercoursePegs',
          prefix: 'optGenitalIntercourse',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Genital Intercourse',
          description: 'Genital intercourse (60s timer)',
          actionText: 'Genital intercourse',
          group: 'adult',
          icon: '🔥',
          color: '#ef4444',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [75, 55, 85, 115], suffix: 'Hole' }
        },
        {
          key: 'touchAnus',
          chkId: 'optTouchAnus',
          countId: 'optTouchAnusCount',
          pegsId: 'optTouchAnusPegs',
          prefix: 'optTouchAnus',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Touch Anus',
          description: 'Touch anus (60s timer)',
          actionText: 'Touch anus',
          group: 'adult',
          icon: '🍑',
          color: '#8b5cf6',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [85, 50, 80, 110], suffix: 'Hole' }
        },
        {
          key: 'lickAnus',
          chkId: 'optLickAnus',
          countId: 'optLickAnusCount',
          pegsId: 'optLickAnusPegs',
          prefix: 'optLickAnus',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Lick Anus',
          description: 'Lick anus (60s timer)',
          actionText: 'Lick anus',
          group: 'adult',
          icon: '👅',
          color: '#ec4899',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [95, 55, 85, 115], suffix: 'Hole' }
        },
        {
          key: 'fingerAnus',
          chkId: 'optFingerAnus',
          countId: 'optFingerAnusCount',
          pegsId: 'optFingerAnusPegs',
          prefix: 'optFingerAnus',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Finger Anus',
          description: 'Finger anus (60s timer)',
          actionText: 'Finger anus',
          group: 'adult',
          icon: '👉',
          color: '#f59e0b',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [105, 60, 90, 120], suffix: 'Hole' }
        },
        {
          key: 'analIntercourse',
          chkId: 'optAnalIntercourse',
          countId: 'optAnalIntercourseCount',
          pegsId: 'optAnalIntercoursePegs',
          prefix: 'optAnalIntercourse',
          subgroup: 'adultChallenges',
          type: 'trackItem',
          name: 'Anal Intercourse',
          description: 'Anal intercourse (60s timer)',
          actionText: 'Anal intercourse',
          group: 'adult',
          icon: '🔥',
          color: '#ef4444',
          maxCount: 4,
          defaultCount: 1,
          defaultPegs: 1,
          timed: true,
          holes: { min: 5, max: 120, step: 5, defaults: [115, 65, 95, 115], suffix: 'Hole' }
        }
      ]
    }
  };

  // Helper getters
  GAME_CONFIGS.getGame = function (brand) {
    return GAME_CONFIGS[brand] || GAME_CONFIGS.crazy;
  };

  GAME_CONFIGS.getOption = function (brand, optKey) {
    const g = GAME_CONFIGS.getGame(brand);
    return (g && g.options) ? g.options.find(o => o.key === optKey || (optKey === 'spinningWheel' && o.key === 'wheel') || (optKey === 'wheel' && o.key === 'spinningWheel')) : null;
  };

  return GAME_CONFIGS;
});
